// Звук голосового оркестратора: захват микрофона и воспроизведение ответа.
//
// Протокол с сервером (docs/orchestrator-spec.md): в обе стороны бинарные кадры
// PCM16LE, 24 кГц, моно. Браузер сам выбирает частоту AudioContext (44,1/48 кГц),
// поэтому захват ресемплится в AudioWorklet, а воспроизведение отдаёт буферы
// 24 кГц — до частоты контекста их пересчитывает сам браузер.
//
// AudioContext создаётся строго в обработчике клика «Запустить» (до первого
// await): iOS иначе оставляет контекст «suspended» и звука не будет.

/** Частота протокола. */
export const RATE = 24_000
/** Длина кадра захвата: 40 мс при 24 кГц. */
const FRAME = 960
/** Запас перед первым кусочком ответа — гасит дрожание сети без слышимой задержки. */
const LEAD_S = 0.06

// Воркет отдаём Blob-URL'ом: отдельного файла в public/ не нужно, а сборка
// Vite не трогает строку. Ресемплинг — «ящичный» фильтр: каждый выходной отсчёт
// — среднее входных за свой интервал. Для речи этого хватает, и он заодно
// срезает алиасинг при 48 → 24 кГц лучше линейной интерполяции.
const WORKLET = `
class AbxCapture extends AudioWorkletProcessor {
  constructor(options) {
    super()
    const target = (options && options.processorOptions && options.processorOptions.rate) || ${RATE}
    this.ratio = sampleRate / target
    this.pos = 0
    this.acc = 0
    this.cnt = 0
    this.last = 0
    this.frame = new Int16Array(${FRAME})
    this.n = 0
    this.sq = 0
  }
  emit(v) {
    const s = v < -1 ? -1 : v > 1 ? 1 : v
    this.frame[this.n++] = s < 0 ? s * 0x8000 : s * 0x7fff
    this.sq += s * s
    if (this.n === this.frame.length) {
      const level = Math.sqrt(this.sq / this.n)
      const out = this.frame
      this.port.postMessage({ pcm: out.buffer, level }, [out.buffer])
      this.frame = new Int16Array(${FRAME})
      this.n = 0
      this.sq = 0
    }
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0]
    if (!ch) return true
    for (let i = 0; i < ch.length; i++) {
      this.acc += ch[i]
      this.cnt++
      this.pos += 1
      while (this.pos >= this.ratio) {
        const v = this.cnt ? this.acc / this.cnt : this.last
        this.last = v
        this.emit(v)
        this.pos -= this.ratio
        this.acc = 0
        this.cnt = 0
      }
    }
    return true
  }
}
registerProcessor('abx-capture', AbxCapture)
`

let workletUrl: string | null = null
function workletModule(): string {
  if (!workletUrl) workletUrl = URL.createObjectURL(new Blob([WORKLET], { type: 'application/javascript' }))
  return workletUrl
}

/** Кусочек ответа в очереди: когда начинается и сколько длится (секунды контекста). */
interface Chunk { node: AudioBufferSourceNode; start: number; dur: number }

export interface AudioHandlers {
  /** кадр PCM16LE 24 кГц для отправки на сервер */
  onFrame: (pcm: ArrayBuffer) => void
}

/** Создать AudioContext. Звать синхронно в обработчике клика. */
export function createContext(): AudioContext {
  const Ctx: typeof AudioContext | undefined = window.AudioContext
    ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctx) throw new Error('браузер не умеет работать со звуком (нет AudioContext)')
  const ctx = new Ctx({ latencyHint: 'interactive' })
  // resume() тоже в рамках жеста — iOS иначе не включит вывод
  void ctx.resume().catch(() => undefined)
  return ctx
}

export class AudioEngine {
  readonly ctx: AudioContext
  private stream: MediaStream | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private worklet: AudioWorkletNode | null = null
  private sink: GainNode | null = null
  private out: GainNode
  private analyser: AnalyserNode
  private probe: Float32Array<ArrayBuffer>
  private queue: Chunk[] = []
  /** кусочки текущего ответа — для подсчёта проигранного */
  private response: Chunk[] = []
  private nextStart = 0
  private freshResponse = true
  private muted = false
  private closed = false
  /** уровень микрофона 0..1 (RMS последнего кадра) */
  micLevel = 0

  constructor(ctx: AudioContext, private handlers: AudioHandlers) {
    this.ctx = ctx
    this.out = ctx.createGain()
    this.analyser = ctx.createAnalyser()
    this.analyser.fftSize = 512
    this.probe = new Float32Array(this.analyser.fftSize)
    this.out.connect(this.analyser)
    this.analyser.connect(ctx.destination)
  }

  /** Поднять микрофон и воркет. Бросает понятную по-русски ошибку. */
  async startCapture(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('микрофон недоступен: панель должна быть открыта по https')
    }
    if (!this.ctx.audioWorklet) {
      throw new Error('браузер не поддерживает AudioWorklet — обновите его')
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      })
    } catch (error) {
      const name = error instanceof DOMException ? error.name : ''
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        throw new Error('нет доступа к микрофону — разрешите его в настройках браузера')
      }
      if (name === 'NotFoundError') throw new Error('микрофон не найден')
      throw new Error(`микрофон не включился: ${error instanceof Error ? error.message : String(error)}`)
    }
    if (this.closed) { this.stopTracks(); return }
    // setMuted(true) мог прийти раньше микрофона (запуск во время записи голосового)
    for (const track of this.stream.getAudioTracks()) track.enabled = !this.muted
    await this.ctx.audioWorklet.addModule(workletModule())
    if (this.closed) { this.stopTracks(); return }
    this.source = this.ctx.createMediaStreamSource(this.stream)
    this.worklet = new AudioWorkletNode(this.ctx, 'abx-capture', {
      numberOfInputs: 1, numberOfOutputs: 1, channelCount: 1, processorOptions: { rate: RATE },
    })
    this.worklet.port.onmessage = (event: MessageEvent<{ pcm: ArrayBuffer; level: number }>) => {
      const { pcm, level } = event.data
      this.micLevel = this.muted ? 0 : level
      // Выключенный микрофон шлёт тишину, а не ничего: GPT-Live ведёт шкалу
      // разговора по входящему звуку и ждёт непрерывный поток.
      if (!this.closed) this.handlers.onFrame(this.muted ? new ArrayBuffer(pcm.byteLength) : pcm)
    }
    // Воркет должен быть подключён к графу, иначе Chrome не вызывает process();
    // выход глушим нулевым усилением, чтобы не слышать себя.
    this.sink = this.ctx.createGain()
    this.sink.gain.value = 0
    this.source.connect(this.worklet)
    this.worklet.connect(this.sink)
    this.sink.connect(this.ctx.destination)
    if (this.ctx.state !== 'running') await this.ctx.resume().catch(() => undefined)
  }

  setMuted(muted: boolean): void {
    this.muted = muted
    for (const track of this.stream?.getAudioTracks() ?? []) track.enabled = !muted
    if (muted) this.micLevel = 0
  }

  /** Кусок ответа PCM16LE 24 кГц → в очередь воспроизведения. */
  play(pcm: ArrayBuffer): void {
    if (this.closed || pcm.byteLength < 2) return
    const samples = new Int16Array(pcm, 0, pcm.byteLength >> 1)
    const buffer = this.ctx.createBuffer(1, samples.length, RATE)
    const data = buffer.getChannelData(0)
    for (let i = 0; i < samples.length; i++) data[i] = samples[i] / 0x8000
    const node = this.ctx.createBufferSource()
    node.buffer = buffer
    node.connect(this.out)
    const now = this.ctx.currentTime
    const drained = this.nextStart <= now
    // Новый ответ: очередь опустела или сервер сказал, что модель думала заново.
    if (this.freshResponse || drained) {
      this.response = []
      this.freshResponse = false
    }
    const start = drained ? now + LEAD_S : this.nextStart
    node.start(start)
    const chunk: Chunk = { node, start, dur: buffer.duration }
    this.nextStart = start + buffer.duration
    this.queue.push(chunk)
    this.response.push(chunk)
    node.onended = () => {
      const i = this.queue.indexOf(chunk)
      if (i >= 0) this.queue.splice(i, 1)
    }
  }

  /** Следующий кусок начнёт новый ответ (сервер перешёл в «думаю»). */
  markNewResponse(): void {
    this.freshResponse = true
  }

  /** Сколько миллисекунд текущего ответа реально прозвучало. */
  playedMs(): number {
    const now = this.ctx.currentTime
    let total = 0
    for (const c of this.response) total += Math.min(Math.max(now - c.start, 0), c.dur)
    return Math.round(total * 1000)
  }

  /** Играет ли что-то прямо сейчас. */
  isPlaying(): boolean {
    return this.nextStart > this.ctx.currentTime && this.queue.length > 0
  }

  /** Перебивание: заглушить всё, что стоит в очереди, и сбросить её. */
  flush(): void {
    for (const c of this.queue) {
      c.node.onended = null
      try { c.node.stop() } catch { /* ещё не стартовал — не страшно */ }
      c.node.disconnect()
    }
    this.queue = []
    this.response = []
    this.nextStart = 0
    this.freshResponse = true
  }

  /** Уровень вывода 0..1 — для пульса круга. */
  outLevel(): number {
    if (!this.isPlaying()) return 0
    this.analyser.getFloatTimeDomainData(this.probe)
    let sq = 0
    for (let i = 0; i < this.probe.length; i++) sq += this.probe[i] * this.probe[i]
    return Math.sqrt(sq / this.probe.length)
  }

  private stopTracks(): void {
    for (const track of this.stream?.getTracks() ?? []) track.stop()
    this.stream = null
  }

  /** Всё остановить: треки, воркет, очередь, контекст. */
  close(): void {
    if (this.closed) return
    this.closed = true
    this.flush()
    if (this.worklet) {
      this.worklet.port.onmessage = null
      this.worklet.disconnect()
    }
    this.source?.disconnect()
    this.sink?.disconnect()
    this.stopTracks()
    void this.ctx.close().catch(() => undefined)
    this.micLevel = 0
  }
}
